import { supabase } from '../lib/supabase';
import openai from '../lib/openai';
import OpenAI, {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

function isSchemaError(error) {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
}

class CharacterService {
  /**
   * Fetch all characters across all plays with statistics
   */
  async getAllCharacters() {
    try {
      // Get all characters from the characters table
      const { data: charactersData, error: charactersError } = await supabase
        ?.from('characters')
        ?.select(`
          id,
          play_id,
          name,
          line_count,
          scene_appearances,
          role_classification,
          description,
          plays(id, title, cover_image_url)
        `)
        ?.order('line_count', { ascending: false });

      if (charactersError) {
        if (isSchemaError(charactersError)) {
          console.warn('Schema cache not ready yet:', charactersError?.message);
          return [];
        } else if (charactersError?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Error fetching characters:', charactersError?.message);
          throw new Error('Failed to fetch character data');
        }
      }

      // Fetch character metadata (images, popularity, AI content)
      const { data: metadataData, error: metadataError } = await supabase
        ?.from('character_metadata')
        ?.select('*');

      if (metadataError && !isSchemaError(metadataError) && metadataError?.code !== 'PGRST116') {
        console.log('Error fetching character metadata:', metadataError?.message);
      }

      const metadataMap = new Map();
      for (const meta of metadataData || []) {
        const key = `${meta?.play_id}-${meta?.character_name}`;
        metadataMap?.set(key, meta);
      }

      // Build character list with enriched data
      const charactersWithData = await Promise.all(
        (charactersData || [])?.map(async (character) => {
          const key = `${character?.play_id}-${character?.name}`;
          const metadata = metadataMap?.get(key);

          // Get voice actor assignment
          const { data: assignmentData } = await supabase
            ?.from('voice_actor_assignments')
            ?.select(`
              voice_actors(
                id,
                stage_name,
                user_profiles(full_name)
              ),
              line_groups!inner(character_name, play_id)
            `)
            ?.eq('line_groups.character_name', character?.name)
            ?.eq('line_groups.play_id', character?.play_id)
            ?.limit(1)
            ?.maybeSingle();

          const voiceActor = assignmentData?.voice_actors;

          return {
            id: character?.id,
            name: character?.name,
            playId: character?.play_id,
            playTitle: character?.plays?.title,
            playImage: character?.plays?.cover_image_url,
            lineCount: character?.line_count,
            sceneAppearances: character?.scene_appearances,
            roleClassification: character?.role_classification,
            description: character?.description,
            imageUrl: metadata?.image_url || null,
            popularityScore: metadata?.popularity_score || 0,
            aiAnalysis: metadata?.ai_analysis || null,
            aiQuotes: metadata?.ai_quotes || null,
            voiceActorName: voiceActor?.stage_name || voiceActor?.user_profiles?.full_name || null,
            voiceActorId: voiceActor?.id || null
          };
        })
      );

      return charactersWithData;
    } catch (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in getAllCharacters:', error?.message);
        throw error;
      }
      console.log('Error in getAllCharacters:', error?.message);
      throw error;
    }
  }

  /**
   * Get detailed character information
   */
  async getCharacterDetails(playId, characterName) {
    try {
      // Get character lines
      const { data: linesData, error: linesError } = await supabase
        ?.from('lines')
        ?.select('id, line_number, dialogue, act:scenes(act_number), scene:scenes(scene_number)')
        ?.eq('play_id', playId)
        ?.eq('character', characterName)
        ?.order('line_number', { ascending: true });

      if (linesError) {
        if (isSchemaError(linesError)) {
          console.error('Schema error:', linesError?.message);
          throw linesError;
        } else if (linesError?.code === 'PGRST116') {
          return null;
        } else {
          console.log('Error fetching character lines:', linesError?.message);
          throw new Error('Failed to fetch character details');
        }
      }

      // Get play information
      const { data: playData, error: playError } = await supabase
        ?.from('plays')
        ?.select('id, title, author, cover_image_url')
        ?.eq('id', playId)
        ?.single();

      if (playError) {
        if (isSchemaError(playError)) {
          console.error('Schema error:', playError?.message);
          throw playError;
        } else {
          console.log('Error fetching play data:', playError?.message);
        }
      }

      // Get character metadata (including AI-generated content)
      const { data: metadataData } = await supabase
        ?.from('character_metadata')
        ?.select('*')
        ?.eq('play_id', playId)
        ?.eq('character_name', characterName)
        ?.maybeSingle();

      // Get voice actor assignment
      const { data: assignmentData } = await supabase
        ?.from('voice_actor_assignments')
        ?.select(`
          voice_actors(
            id,
            stage_name,
            headshot_url,
            user_profiles(full_name)
          ),
          line_groups!inner(character_name, play_id)
        `)
        ?.eq('line_groups.character_name', characterName)
        ?.eq('line_groups.play_id', playId)
        ?.limit(1)
        ?.maybeSingle();

      const voiceActor = assignmentData?.voice_actors;

      // Calculate scene appearances
      const uniqueScenes = new Set();
      for (const line of linesData || []) {
        if (line?.act?.act_number && line?.scene?.scene_number) {
          uniqueScenes?.add(`${line?.act?.act_number}-${line?.scene?.scene_number}`);
        }
      }

      return {
        name: characterName,
        playId,
        playTitle: playData?.title,
        playAuthor: playData?.author,
        playImage: playData?.cover_image_url,
        lineCount: linesData?.length || 0,
        sceneAppearances: uniqueScenes?.size,
        imageUrl: metadataData?.image_url || null,
        popularityScore: metadataData?.popularity_score || 0,
        aiAnalysis: metadataData?.ai_analysis || null,
        aiQuotes: metadataData?.ai_quotes || null,
        voiceActor: voiceActor ? {
          id: voiceActor?.id,
          name: voiceActor?.stage_name || voiceActor?.user_profiles?.full_name,
          headshotUrl: voiceActor?.headshot_url
        } : null,
        lines: linesData || []
      };
    } catch (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in getCharacterDetails:', error?.message);
        throw error;
      }
      console.log('Error in getCharacterDetails:', error?.message);
      throw error;
    }
  }

  /**
   * Generate character analysis using OpenAI and save to database
   */
  async generateCharacterAnalysis(characterName, playTitle, characterLines, playId) {
    try {
      const sampleLines = characterLines?.slice(0, 10)?.map(l => l?.dialogue)?.join('\n');

      const prompt = `You are a Shakespeare scholar. Analyze the character "${characterName}" from "${playTitle}".

Sample dialogue:
${sampleLines}

Provide a comprehensive character analysis including:
1. Personality traits and characteristics
2. Role in the play (protagonist, antagonist, supporting character)
3. Character arc and development
4. Key relationships with other characters
5. Thematic significance

Keep the analysis concise but insightful (300-400 words).`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'You are an expert Shakespeare scholar providing educational character analysis for students.'
          },
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      const analysis = response?.choices?.[0]?.message?.content;

      // Save analysis to database
      const { error: upsertError } = await supabase
        ?.from('character_metadata')
        ?.upsert({
          play_id: playId,
          character_name: characterName,
          ai_analysis: analysis
        }, {
          onConflict: 'play_id,character_name'
        });

      if (upsertError) {
        console.log('Error saving analysis to database:', upsertError?.message);
        // Don't throw - return the analysis even if save fails
      }

      return analysis;
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating character analysis:', error);
      }
      throw new Error(errorInfo?.message);
    }
  }

  /**
   * Generate curated iconic quotes, soliloquies, and signature dialogue for a character and save to database
   */
  async generateIconicQuotes(characterName, playTitle, characterLines, playId) {
    try {
      // Get all character dialogue
      const allDialogue = characterLines?.map((l, idx) => `[${idx}] ${l?.dialogue}`)?.join('\n');

      const prompt = `You are a Shakespeare scholar. Analyze the character "${characterName}" from "${playTitle}" and identify their most iconic quotes, famous soliloquies, and signature dialogue.

Character's lines:
${allDialogue}

Provide a curated list of 5-8 of the most significant quotes that showcase:
1. The character's voice and personality
2. Key thematic contributions
3. Famous soliloquies or monologues
4. Memorable dialogue that defines the character
5. Lines that reveal character development

For each quote, provide:
- The exact quote text (match it EXACTLY from the lines above)
- The line index number in square brackets (e.g., [5])
- A brief explanation (20-30 words) of why this quote is significant
- A category: "Soliloquy", "Iconic Quote", "Signature Dialogue", or "Character Defining"

Format your response as a JSON array with this structure:
[
  {
    "quote": "exact quote text",
    "lineIndex": 5,
    "significance": "explanation of why this is important",
    "category": "Soliloquy"
  }
]

IMPORTANT: Return ONLY the JSON array, no additional text.`;

      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'You are an expert Shakespeare scholar. Return only valid JSON arrays with no additional text or markdown formatting.'
          },
          {
            role: 'user',
            content: prompt
          }
        ]
      });

      const content = response?.choices?.[0]?.message?.content;
      
      // Parse JSON response
      let quotes;
      try {
        // Remove markdown code blocks if present
        const cleanContent = content?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
        quotes = JSON.parse(cleanContent);
      } catch (parseError) {
        console.error('Error parsing quotes JSON:', parseError);
        throw new Error('Failed to parse AI response');
      }

      // Map line indices to actual line data
      const quotesWithLineData = quotes?.map(quote => {
        const lineData = characterLines?.[quote?.lineIndex];
        return {
          ...quote,
          lineId: lineData?.id,
          act: lineData?.act?.act_number,
          scene: lineData?.scene?.scene_number
        };
      });

      // Save quotes to database
      const { error: upsertError } = await supabase
        ?.from('character_metadata')
        ?.upsert({
          play_id: playId,
          character_name: characterName,
          ai_quotes: quotesWithLineData
        }, {
          onConflict: 'play_id,character_name'
        });

      if (upsertError) {
        console.log('Error saving quotes to database:', upsertError?.message);
        // Don't throw - return the quotes even if save fails
      }

      return quotesWithLineData;
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating iconic quotes:', error);
      }
      throw new Error(errorInfo?.message);
    }
  }

  /**
   * Fetch audio recordings for specific line IDs
   */
  async getAudioForLines(lineIds) {
    try {
      if (!lineIds || lineIds?.length === 0) {
        return [];
      }

      const { data, error } = await supabase
        ?.from('audio_recordings')
        ?.select('line_id, audio_url, duration_seconds')
        ?.in('line_id', lineIds);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Error fetching audio recordings:', error?.message);
          throw new Error('Failed to fetch audio recordings');
        }
      }

      // Create a map of line_id to audio data
      const audioMap = new Map();
      for (const audio of data || []) {
        audioMap?.set(audio?.line_id, {
          audioUrl: audio?.audio_url,
          duration: audio?.duration_seconds
        });
      }

      return audioMap;
    } catch (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in getAudioForLines:', error?.message);
        throw error;
      }
      console.log('Error in getAudioForLines:', error?.message);
      throw error;
    }
  }

  /**
   * Upload character image
   */
  async uploadCharacterImage(playId, characterName, file) {
    try {
      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only JPEG, PNG, and WebP are allowed.');
      }

      if (file?.size > 5242880) {
        throw new Error('File size must be less than 5MB');
      }

      // Create file path
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${playId}-${characterName?.replace(/\s+/g, '-')}-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      // Get existing metadata to check for old image
      const { data: existingMeta } = await supabase
        ?.from('character_metadata')
        ?.select('image_url')
        ?.eq('play_id', playId)
        ?.eq('character_name', characterName)
        ?.maybeSingle();

      // Delete old image if exists
      if (existingMeta?.image_url) {
        const oldPath = existingMeta?.image_url?.split('/')?.pop();
        if (oldPath) {
          await supabase?.storage?.from('character-images')?.remove([oldPath]);
        }
      }

      // Upload new image
      const { error: uploadError } = await supabase?.storage
        ?.from('character-images')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        if (isSchemaError(uploadError)) {
          console.error('Schema error:', uploadError?.message);
          throw uploadError;
        } else {
          console.log('Upload error:', uploadError?.message);
          throw new Error('Failed to upload image');
        }
      }

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from('character-images')
        ?.getPublicUrl(filePath);

      const imageUrl = urlData?.publicUrl;

      // Update or create character metadata
      const { error: upsertError } = await supabase
        ?.from('character_metadata')
        ?.upsert({
          play_id: playId,
          character_name: characterName,
          image_url: imageUrl
        }, {
          onConflict: 'play_id,character_name'
        });

      if (upsertError) {
        if (isSchemaError(upsertError)) {
          console.error('Schema error:', upsertError?.message);
          throw upsertError;
        } else {
          console.log('Metadata update error:', upsertError?.message);
          throw new Error('Failed to update character metadata');
        }
      }

      return imageUrl;
    } catch (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in uploadCharacterImage:', error?.message);
        throw error;
      }
      console.log('Error in uploadCharacterImage:', error?.message);
      throw error;
    }
  }

  /**
   * Update character popularity score
   */
  async updatePopularityScore(playId, characterName, increment = 1) {
    try {
      const { data: existingMeta } = await supabase
        ?.from('character_metadata')
        ?.select('popularity_score')
        ?.eq('play_id', playId)
        ?.eq('character_name', characterName)
        ?.maybeSingle();

      const newScore = (existingMeta?.popularity_score || 0) + increment;

      const { error } = await supabase
        ?.from('character_metadata')
        ?.upsert({
          play_id: playId,
          character_name: characterName,
          popularity_score: newScore
        }, {
          onConflict: 'play_id,character_name'
        });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Popularity update error:', error?.message);
        }
      }

      return newScore;
    } catch (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in updatePopularityScore:', error?.message);
        throw error;
      }
      console.log('Error in updatePopularityScore:', error?.message);
      throw error;
    }
  }

  /**
   * Get character relationships based on shared scenes
   */
  async getCharacterRelationships(playId, characterName) {
    try {
      // Get all scenes where the target character appears
      const { data: targetScenes, error: targetError } = await supabase
        ?.from('lines')
        ?.select('scene_id, scenes(act_number, scene_number)')
        ?.eq('play_id', playId)
        ?.eq('character', characterName);

      if (targetError) {
        if (isSchemaError(targetError)) {
          console.error('Schema error:', targetError?.message);
          throw targetError;
        } else {
          console.log('Error fetching target character scenes:', targetError?.message);
          throw new Error('Failed to fetch character scenes');
        }
      }

      const sceneIds = [...new Set(targetScenes?.map(s => s?.scene_id)?.filter(Boolean))];

      if (sceneIds?.length === 0) {
        return { nodes: [], links: [] };
      }

      // Get all characters that appear in those scenes
      const { data: relatedLines, error: relatedError } = await supabase
        ?.from('lines')
        ?.select('character, scene_id')
        ?.eq('play_id', playId)
        ?.in('scene_id', sceneIds)
        ?.not('character', 'is', null);

      if (relatedError) {
        if (isSchemaError(relatedError)) {
          console.error('Schema error:', relatedError?.message);
          throw relatedError;
        } else {
          console.log('Error fetching related characters:', relatedError?.message);
          throw new Error('Failed to fetch character relationships');
        }
      }

      // Build relationship map
      const relationshipMap = new Map();
      const characterScenes = new Map();

      for (const line of relatedLines || []) {
        if (!characterScenes?.has(line?.character)) {
          characterScenes?.set(line?.character, new Set());
        }
        characterScenes?.get(line?.character)?.add(line?.scene_id);
      }

      // Calculate shared scenes between characters
      const characters = Array.from(characterScenes?.keys());
      for (let i = 0; i < characters?.length; i++) {
        for (let j = i + 1; j < characters?.length; j++) {
          const char1 = characters?.[i];
          const char2 = characters?.[j];
          const scenes1 = characterScenes?.get(char1);
          const scenes2 = characterScenes?.get(char2);
          const sharedScenes = [...scenes1]?.filter(s => scenes2?.has(s));

          if (sharedScenes?.length > 0) {
            const key = [char1, char2]?.sort()?.join('-');
            relationshipMap?.set(key, {
              source: char1,
              target: char2,
              value: sharedScenes?.length
            });
          }
        }
      }

      // Get character metadata for images
      const { data: metadataData } = await supabase
        ?.from('character_metadata')
        ?.select('character_name, image_url')
        ?.eq('play_id', playId)
        ?.in('character_name', characters);

      const metadataMap = new Map();
      for (const meta of metadataData || []) {
        metadataMap?.set(meta?.character_name, meta?.image_url);
      }

      // Build nodes
      const nodes = characters?.map(char => ({
        id: char,
        name: char,
        lineCount: relatedLines?.filter(l => l?.character === char)?.length,
        isTarget: char === characterName,
        imageUrl: metadataMap?.get(char) || null
      }));

      // Build links
      const links = Array.from(relationshipMap?.values());

      return { nodes, links };
    } catch (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in getCharacterRelationships:', error?.message);
        throw error;
      }
      console.log('Error in getCharacterRelationships:', error?.message);
      throw error;
    }
  }

  /**
   * Create a new character (admin only)
   */
  async createCharacter(playId, characterData) {
    try {
      const { data, error } = await supabase
        ?.from('characters')
        ?.insert([{
          play_id: playId,
          name: characterData?.name,
          line_count: characterData?.lineCount || 0,
          scene_appearances: characterData?.sceneAppearances || 0,
          role_classification: characterData?.roleClassification || 'supporting',
          description: characterData?.description || null
        }])
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error?.message || 'Failed to create character');
      }

      return data;
    } catch (error) {
      console.error('Error creating character:', error);
      throw error;
    }
  }

  /**
   * Update an existing character (admin only)
   */
  async updateCharacter(characterId, updates) {
    try {
      const updateData = {};
      if (updates?.name !== undefined) updateData.name = updates?.name;
      if (updates?.lineCount !== undefined) updateData.line_count = updates?.lineCount;
      if (updates?.sceneAppearances !== undefined) updateData.scene_appearances = updates?.sceneAppearances;
      if (updates?.roleClassification !== undefined) updateData.role_classification = updates?.roleClassification;
      if (updates?.description !== undefined) updateData.description = updates?.description;

      const { data, error } = await supabase
        ?.from('characters')
        ?.update(updateData)
        ?.eq('id', characterId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error?.message || 'Failed to update character');
      }

      return data;
    } catch (error) {
      console.error('Error updating character:', error);
      throw error;
    }
  }

  /**
   * Delete a character (admin only)
   */
  async deleteCharacter(characterId) {
    try {
      const { error } = await supabase
        ?.from('characters')
        ?.delete()
        ?.eq('id', characterId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error?.message || 'Failed to delete character');
      }

      return true;
    } catch (error) {
      console.error('Error deleting character:', error);
      throw error;
    }
  }

  /**
   * Get character by ID
   */
  async getCharacterById(characterId) {
    try {
      const { data, error } = await supabase
        ?.from('characters')
        ?.select(`
          id,
          play_id,
          name,
          line_count,
          scene_appearances,
          role_classification,
          description,
          created_at,
          updated_at,
          plays(id, title, author, cover_image_url)
        `)
        ?.eq('id', characterId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error?.message || 'Failed to fetch character');
      }

      return {
        id: data?.id,
        playId: data?.play_id,
        name: data?.name,
        lineCount: data?.line_count,
        sceneAppearances: data?.scene_appearances,
        roleClassification: data?.role_classification,
        description: data?.description,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        play: data?.plays
      };
    } catch (error) {
      console.error('Error fetching character by ID:', error);
      throw error;
    }
  }

  /**
   * Get all characters for a specific play
   */
  async getCharactersByPlayId(playId) {
    try {
      const { data, error } = await supabase
        ?.from('characters')
        ?.select('*')
        ?.eq('play_id', playId)
        ?.order('line_count', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error?.message || 'Failed to fetch characters');
      }

      return data?.map(char => ({
        id: char?.id,
        playId: char?.play_id,
        name: char?.name,
        lineCount: char?.line_count,
        sceneAppearances: char?.scene_appearances,
        roleClassification: char?.role_classification,
        description: char?.description,
        createdAt: char?.created_at,
        updatedAt: char?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching characters by play ID:', error);
      throw error;
    }
  }
}

export default new CharacterService();